import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowRight,
  Sparkles,
  Layers,
  Play,
  Scissors,
  Check,
  X,
  MessageCircle,
  BookOpen,
  Download,
  FileText,
  Eye,
  Package,
  Loader2,
  Users
} from 'lucide-react';
import PublicHeader, { PUBLIC_HEADER_TAB_STORAGE_KEY } from './PublicHeader';
import AtelierLanding from './landing/atelier/AtelierLanding';
import AtelierPricing from './landing/atelier/AtelierPricing';
import { ATELIER_SCROLL_TARGET_KEY } from './landing/atelier/AtelierChrome';
import CalendlyDemoModal from './CalendlyDemoModal';
import MascotHelp from './mascot/MascotHelp';
import './landing/luxe/luxe.css';
import { getAllPublicKits } from '../api/kit.api';
import { getAllPublicFabrics } from '../api/product.api';
import { toThumbnailUrl } from '../utils/imageThumbnailUrl';
import {
  PRICING_PLANS,
  resolvePlanBillingCycle,
  getVisiblePricingPlans,
} from '../config/pricingPlans';
import { openRazorpayPricingCheckout, setPendingPricingCheckout } from '../utils/pricingRazorpay';
import { femaleInspirationUrl } from '../data/femaleInspirationDesigns';
import { SESSION_OPEN_PUBLIC_FABRIC_ID } from '../constants/publicFabricNavigation';

const MobileAutoCarousel = ({ images, interval = 2500 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!images || images.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, interval);

    return () => clearInterval(timer);
  }, [images, interval]);

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full overflow-hidden rounded-lg shadow-md">
      {images.map((image, index) => (
        <img
          key={image}
          src={image}
          alt="Generated concept"
          className={`w-full aspect-square object-cover transition-opacity duration-700 ${
            index === currentIndex ? 'opacity-100 relative' : 'opacity-0 absolute inset-0'
          }`}
        />
      ))}
      {images.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
          {images.map((_, index) => (
            <span
              key={index}
              className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-white/90 w-3' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const LandingPage = ({ setActiveView, openRequestAccessModal, showNotification, onSupportClick }) => {
    const isAdmin = localStorage.getItem('role')?.toLowerCase() === 'admin';
    const [pricingBillingTab, setPricingBillingTab] = useState('monthly');
    const visiblePricingPlans = getVisiblePricingPlans(isAdmin, pricingBillingTab);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [generatedKits, setGeneratedKits] = useState([]);
    const [isLoadingKits, setIsLoadingKits] = useState(true);
    const [selectedKit, setSelectedKit] = useState(null);
    const [publicFabrics, setPublicFabrics] = useState([]);
    const [isLoadingFabrics, setIsLoadingFabrics] = useState(true);
    const [landingGalleryTab, setLandingGalleryTab] = useState('fabrics'); // 'fabrics' | 'bodycuts' | 'designs'
    const [landingHeaderTab, setLandingHeaderTab] = useState('home'); // 'home' | 'gallery' | 'howitworks' | 'pricing'
    const [payingPlanId, setPayingPlanId] = useState(null);
    const [showCalendlyDemo, setShowCalendlyDemo] = useState(false);

    const notify = useCallback(
      (message, type = 'info') => {
        if (showNotification) showNotification(message, type);
        else if (type === 'error') console.error(message);
        else console.log(message);
      },
      [showNotification]
    );

    const handlePublicPricingPurchase = useCallback(
      (plan) => {
        const token = localStorage.getItem('token');
        const billingCycle = resolvePlanBillingCycle(plan);
        if (!token) {
          setPendingPricingCheckout(plan.id, billingCycle);
          notify('Sign in to complete your purchase. We saved your plan choice.', 'info');
          openRequestAccessModal?.();
          return;
        }
        setPayingPlanId(plan.id);
        openRazorpayPricingCheckout({
          plan,
          billingCycle,
          token,
          onNotify: notify,
          onFinally: () => setPayingPlanId(null),
        });
      },
      [notify, openRequestAccessModal]
    );

    const showLandingGallery = landingHeaderTab === 'gallery';

    const bodyCutsPreview = [
        { name: 'Body Con Dress', imageUrl: femaleInspirationUrl('Dresses/Body Con.png') },
        { name: 'Body Fit Dress', imageUrl: femaleInspirationUrl('Dresses/Body Fit.png') },
        { name: 'Halter Dress', imageUrl: femaleInspirationUrl('Dresses/Halter dress.png') },
        { name: 'One Shoulder Dress', imageUrl: femaleInspirationUrl('Dresses/One Shoulder Dress.png') },
        { name: 'Halter Top', imageUrl: femaleInspirationUrl('Tops/Halter.png') },
        { name: 'Off Shoulder Top', imageUrl: femaleInspirationUrl('Tops/Off shoulder.png') },
        { name: 'Palazzo', imageUrl: femaleInspirationUrl('Bottom/Palazzo.png') },
        { name: 'High Waist', imageUrl: femaleInspirationUrl('Bottom/High Waist.png') },
        { name: 'Maxi Skirt', imageUrl: femaleInspirationUrl('Skirts/maxi cut skirt.png') },
        { name: 'Mermaid Skirt', imageUrl: femaleInspirationUrl('Skirts/mermaid skirt.png') },
        { name: 'Bardot Jumpsuit', imageUrl: femaleInspirationUrl('Jumpsuits/Bardot jumpsuit.png') },
        { name: 'Anarkali', imageUrl: femaleInspirationUrl('Kurti suit/Anarkali.png') },
    ];
    // Unified WhatsApp opener: tries deep link first, falls back to web link
    const openWhatsApp = (message) => {
        const phone = '919873009199'; // 91 + 9873009199
        const encoded = encodeURIComponent(message || 'Hi! I want to know more about Fashdeck');
        const deepLink = `whatsapp://send?phone=${phone}&text=${encoded}`;
        const webLink = `https://wa.me/${phone}?text=${encoded}`;
        // Try deep link for mobile apps
        try {
            window.location.href = deepLink;
            // Fallback to web after a short delay (in case app isn't installed)
            setTimeout(() => {
                window.open(webLink, '_blank', 'noopener,noreferrer');
            }, 600);
        } catch (e) {
            window.open(webLink, '_blank', 'noopener,noreferrer');
        }
    };

    // Fetch generated kits
    useEffect(() => {
        const fetchKits = async () => {
            setIsLoadingKits(true);
            try {
                const response = await getAllPublicKits(1, 24, '', '', true); // Only kits flagged for landing page
                setGeneratedKits(response.kits || []);
            } catch (error) {
                console.error('Error fetching generated kits:', error);
                setGeneratedKits([]);
            } finally {
                setIsLoadingKits(false);
            }
        };
        fetchKits();
    }, []);

    // Fetch public fabrics
    useEffect(() => {
        const fetchFabrics = async () => {
            setIsLoadingFabrics(true);
            try {
                const response = await getAllPublicFabrics(1, 24); // Get first 24 fabrics
                setPublicFabrics(response.products || []);
            } catch (error) {
                console.error('Error fetching public fabrics:', error);
                setPublicFabrics([]);
            } finally {
                setIsLoadingFabrics(false);
            }
        };
        fetchFabrics();
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [landingHeaderTab]);

    // When returning to home from other public pages, we preserve the tab selection.
    useEffect(() => {
        try {
            const saved = window.sessionStorage.getItem(PUBLIC_HEADER_TAB_STORAGE_KEY);
            if (saved && saved !== landingHeaderTab) {
                setLandingHeaderTab(saved);
            }
            window.sessionStorage.removeItem(PUBLIC_HEADER_TAB_STORAGE_KEY);
        } catch (_) {}
        // Only run once on mount
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (landingHeaderTab !== 'gallery') return;
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [landingGalleryTab, landingHeaderTab]);

    const goHomeSection = (id) => () => {
        try { window.sessionStorage.setItem(ATELIER_SCROLL_TARGET_KEY, id); } catch (_) {}
        setLandingHeaderTab('home');
    };

    const handleMascotNavigate = (view) => {
        if (view === 'pricing') {
            setLandingHeaderTab('pricing');
            return;
        }
        setActiveView('login');
    };

    const mascotHelp = (
        <MascotHelp
            signedIn={false}
            pageId={landingHeaderTab === 'pricing' ? 'pricing' : 'home'}
            hidden={showCalendlyDemo}
            onNavigate={handleMascotNavigate}
            onRequestSupport={onSupportClick}
        />
    );

    if (landingHeaderTab === 'pricing') {
        return (
            <>
            <AtelierPricing
                plans={visiblePricingPlans}
                billingTab={pricingBillingTab}
                onBillingTabChange={setPricingBillingTab}
                payingPlanId={payingPlanId}
                onPurchase={handlePublicPricingPurchase}
                navLinks={[
                    ['Fabric library', goHomeSection('library')],
                    ['AI Studio', goHomeSection('studio')],
                    ['Catalogues', goHomeSection('catalogues')],
                    ['For suppliers', goHomeSection('suppliers')],
                    ['About', () => setActiveView('about-us')],
                    ['Pricing', () => window.scrollTo({ top: 0, behavior: 'smooth' }), true],
                ]}
                onLogo={() => setLandingHeaderTab('home')}
                onSignIn={() => setActiveView('login')}
                onStartDesigning={() => setActiveView('login')}
                onAbout={() => setActiveView('about-us')}
                onHelp={onSupportClick}
                onPrivacy={() => setActiveView('privacy-policy')}
                onBookDemo={() => setShowCalendlyDemo(true)}
            />
            <CalendlyDemoModal isOpen={showCalendlyDemo} onClose={() => setShowCalendlyDemo(false)} />
            {mascotHelp}
            </>
        );
    }

    if (landingHeaderTab === 'home') {
        return (
            <>
            <AtelierLanding
                onStartDesigning={() => setActiveView('login')}
                onSignIn={() => setActiveView('login')}
                onSupplierSignup={() => setActiveView('signup')}
                onOpenCatalogue={() => setActiveView('browse-vendors')}
                onPricing={() => setLandingHeaderTab('pricing')}
                onAbout={() => setActiveView('about-us')}
                onPrivacy={() => setActiveView('privacy-policy')}
                onHelp={onSupportClick}
            />
            {mascotHelp}
            </>
        );
    }

    return (
        <>
        <div className="luxe-root min-h-screen">
            {/* Glassmorphic Navigation */}
            <PublicHeader
                activeTab={landingHeaderTab}
                onTabChange={setLandingHeaderTab}
                setActiveView={setActiveView}
                onLaunchAI={() => setActiveView('login')}
                onSupportClick={onSupportClick}
                variant="dark"
            />

            {showLandingGallery && (
            <section className="relative pb-24 px-6 pt-24 sm:pt-28 luxe-bg-gradient luxe-grain min-h-screen">
                <div className="luxe-aurora" />
                <div className="relative container mx-auto max-w-7xl">
                    <div className="text-center mb-10">
                        <p className="luxe-eyebrow mb-4">Gallery</p>
                        <h1 className="luxe-display text-4xl sm:text-5xl text-white">
                            The Fashdeck <span className="luxe-gold-text">Archive</span>
                        </h1>
                    </div>

                    {/* Tab Switcher */}
                    <div className="flex justify-center mb-10">
                        <div className="inline-flex items-center rounded-full border border-white/12 bg-white/5 backdrop-blur p-1">
                            {[
                                { id: 'fabrics', label: 'Fabric Gallery', Icon: Layers, sep: '+' },
                                { id: 'bodycuts', label: 'Body Cuts', Icon: Scissors, sep: '=' },
                                { id: 'designs', label: 'Generated Designs', Icon: Sparkles, sep: null },
                            ].map(({ id, label, Icon, sep }) => (
                                <React.Fragment key={id}>
                                    <button
                                        type="button"
                                        onClick={() => setLandingGalleryTab(id)}
                                        className={`px-4 sm:px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                                            landingGalleryTab === id
                                                ? 'bg-gold text-white'
                                                : 'text-white/60 hover:text-white hover:bg-white/10'
                                        }`}
                                    >
                                        <span className="inline-flex items-center gap-2">
                                            <Icon className="w-4 h-4" aria-hidden="true" />
                                            {label}
                                        </span>
                                    </button>
                                    {sep && (
                                        <span className="px-1 sm:px-2 flex items-center text-gold/60 font-bold select-none" aria-hidden="true">
                                            {sep}
                                        </span>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Tab Panel: Generated Designs */}
                    {landingGalleryTab === 'designs' && (
                        <div>
                            {isLoadingKits ? (
                                <div className="flex items-center justify-center py-20">
                                    <Loader2 className="w-8 h-8 text-gold animate-spin" />
                                    <span className="ml-3 text-white/50">Loading designs...</span>
                                </div>
                            ) : generatedKits.length === 0 ? (
                                <div className="text-center py-20">
                                    <Package className="w-16 h-16 text-white/15 mx-auto mb-4" />
                                    <p className="text-white/40">No designs available yet</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                    {generatedKits.map((kit) => {
                                        const kitImage =
                                            kit.generatedImageUrl || kit.productDetails?.image || kit.thumbnail;
                                        if (!kitImage) return null;
                                        const kitThumb = toThumbnailUrl(kitImage);

                                        return (
                                            <div
                                                key={kit._id}
                                                className="group relative luxe-glass rounded-xl overflow-hidden cursor-pointer transition-all duration-500 hover:luxe-hairline-gold hover:shadow-[0_20px_60px_-20px_rgba(155, 138, 196,0.35)]"
                                                onClick={() => setSelectedKit(kit)}
                                            >
                                                <div className="aspect-[3/4] bg-white/5 relative overflow-hidden">
                                                    <img
                                                        src={kitThumb}
                                                        alt={kit.name || kit.productDetails?.name || 'Generated Design'}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                                        onError={(e) => {
                                                            if (e.currentTarget.src !== kitImage) {
                                                                e.currentTarget.src = kitImage;
                                                            } else {
                                                                e.currentTarget.style.display = 'none';
                                                            }
                                                        }}
                                                    />
                                                    {kit.status === 'generated' && (
                                                        <div className="absolute top-2 right-2 bg-green-600 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-lg">
                                                            ✓
                                                        </div>
                                                    )}
                                                    {kit.videoUrl && (
                                                        <div className="absolute top-2 left-2 bg-slate-900 text-white p-1.5 rounded-full shadow-lg">
                                                            <Play size={12} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {generatedKits.length % 6 === 5 && (
                                        <button
                                            type="button"
                                            onClick={() => setActiveView('ai-gallery')}
                                            className="group relative luxe-glass rounded-xl overflow-hidden transition-all duration-500 hover:luxe-hairline-gold text-left"
                                            aria-label="View all generated designs"
                                        >
                                            <div className="aspect-[3/4] relative overflow-hidden flex items-center justify-center">
                                                <div className="relative p-4 w-full h-full flex flex-col items-center justify-center gap-3">
                                                    <div className="w-12 h-12 rounded-full bg-gold text-white flex items-center justify-center">
                                                        <ArrowRight className="w-6 h-6 group-hover:translate-x-0.5 transition-transform" />
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-sm font-bold text-white">View all</p>
                                                        <p className="text-xs text-white/50 mt-1">Generated designs</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="text-center mt-10 sm:mt-12 pt-8 border-t border-white/10">
                                <p className="text-lg text-white/50 font-light max-w-2xl mx-auto mb-6">
                                    Explore stunning fashion designs created by our community using AI-powered tools
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setActiveView('ai-gallery')}
                                    className="luxe-btn-gold group"
                                >
                                    View All Designs
                                    <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Tab Panel: Fabric Gallery */}
                    {landingGalleryTab === 'fabrics' && (
                        <div>
                            {isLoadingFabrics ? (
                                <div className="flex items-center justify-center py-20">
                                    <Loader2 className="w-8 h-8 text-gold animate-spin" />
                                    <span className="ml-3 text-white/50">Loading fabrics...</span>
                                </div>
                            ) : publicFabrics.length === 0 ? (
                                <div className="text-center py-20">
                                    <Layers className="w-16 h-16 text-white/15 mx-auto mb-4" />
                                    <p className="text-white/40">No fabrics available yet</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                    {publicFabrics.map((fabric) => {
                                        const fabricImg = fabric.image ||
                                            fabric.images?.original?.s3Url ||
                                            fabric.images?.original?.imgbbUrl ||
                                            fabric.images?.original?.url ||
                                            'https://via.placeholder.com/300x300?text=No+Image';
                                        const fabricThumb = toThumbnailUrl(fabricImg);

                                        const fabricId = fabric._id ?? fabric.id;
                                        return (
                                            <div
                                                key={fabricId || fabric.name}
                                                role="button"
                                                tabIndex={0}
                                                className="group relative luxe-glass rounded-xl overflow-hidden cursor-pointer transition-all duration-500 hover:luxe-hairline-gold hover:shadow-[0_20px_60px_-20px_rgba(155, 138, 196,0.35)]"
                                                onClick={() => {
                                                    if (fabricId) {
                                                        try {
                                                            sessionStorage.setItem(
                                                                SESSION_OPEN_PUBLIC_FABRIC_ID,
                                                                String(fabricId),
                                                            );
                                                        } catch (_) {}
                                                    }
                                                    setActiveView('fabricmarketplace');
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        if (fabricId) {
                                                            try {
                                                                sessionStorage.setItem(
                                                                    SESSION_OPEN_PUBLIC_FABRIC_ID,
                                                                    String(fabricId),
                                                                );
                                                            } catch (_) {}
                                                        }
                                                        setActiveView('fabricmarketplace');
                                                    }
                                                }}
                                            >
                                                <div className="aspect-square bg-white/5 relative overflow-hidden">
                                                    <img
                                                        src={fabricThumb}
                                                        alt={fabric.name || 'Fabric'}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                                        onError={(e) => {
                                                            if (e.currentTarget.src !== fabricImg) {
                                                                e.currentTarget.src = fabricImg;
                                                            } else {
                                                                e.target.style.display = 'none';
                                                            }
                                                        }}
                                                    />
                                                    <div className="absolute inset-0 flex items-center justify-center bg-noir/0 group-hover:bg-noir/30 transition-all duration-300">
                                                        <div className="bg-noir/80 backdrop-blur-sm px-3 py-2 rounded-lg border border-gold/40 transform scale-95 group-hover:scale-100 transition-transform opacity-0 group-hover:opacity-100">
                                                            <div className="flex items-center gap-2 text-sm font-semibold text-gold">
                                                                <Eye size={16} />
                                                                <span>View</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {fabric.name && (
                                                    <div className="p-2">
                                                        <p className="text-xs text-white/60 truncate">{fabric.name}</p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="text-center mt-10 sm:mt-12 pt-8 border-t border-white/10">
                                <p className="text-lg text-white/50 font-light max-w-2xl mx-auto mb-6">
                                    Discover premium fabrics from verified suppliers and collections
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                                    <button
                                        type="button"
                                        onClick={() => setActiveView('fabricmarketplace')}
                                        className="luxe-btn-gold group"
                                    >
                                        View all fabrics
                                        <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                    {isAdmin ? (
                                      <button
                                          type="button"
                                          onClick={() => setActiveView('browse-vendors')}
                                          className="luxe-btn-ghost group"
                                      >
                                          <Users className="w-5 h-5 text-gold" />
                                          Browse vendors
                                      </button>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tab Panel: Body Cuts (Inspiration Designs) */}
                    {landingGalleryTab === 'bodycuts' && (
                        <div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                {bodyCutsPreview.map((item) => (
                                    <div
                                        key={item.imageUrl}
                                        className="group relative luxe-glass rounded-xl overflow-hidden cursor-pointer transition-all duration-500 hover:luxe-hairline-gold hover:shadow-[0_20px_60px_-20px_rgba(155, 138, 196,0.35)]"
                                        onClick={() => setActiveView('inspirational')}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setActiveView('inspirational');
                                            }
                                        }}
                                        aria-label={`Open body cut: ${item.name}`}
                                    >
                                        <div className="aspect-square bg-white/90 relative overflow-hidden">
                                            <img
                                                src={item.imageUrl}
                                                alt={item.name}
                                                className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300 p-2"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                            <div className="absolute inset-0 flex items-end justify-start bg-gradient-to-t from-noir/80 via-noir/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                                <div className="p-2">
                                                    <p className="text-xs font-semibold text-gold line-clamp-2">{item.name}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="text-center mt-10 sm:mt-12 pt-8 border-t border-white/10">
                                <p className="text-lg text-white/50 font-light max-w-2xl mx-auto mb-6">
                                    Explore inspiration body cuts to kickstart your next collection
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setActiveView('inspirational')}
                                    className="luxe-btn-gold group"
                                >
                                    View All Body Cuts
                                    <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>
            )}

            {landingHeaderTab === 'howitworks' && (
            <>
            <section className="relative pt-24 sm:pt-28 py-20 px-6 luxe-bg-gradient luxe-grain min-h-[50vh]">
                <div className="luxe-aurora" />
                <div className="relative container mx-auto max-w-6xl">
                    <div className="text-center space-y-5 mb-16">
                        <p className="luxe-eyebrow">Process</p>
                        <h2 className="luxe-display text-4xl md:text-5xl text-white">
                            How It <span className="luxe-gold-text">Works</span>
                        </h2>
                        <p className="text-xl text-white/50 font-light">
                            From AI design to fabric sourcing in three simple steps
                        </p>
                    </div>

                    <div className="max-w-5xl mx-auto">
                        <div className="relative rounded-[20px] overflow-hidden luxe-glass luxe-hairline-gold">
                            <video 
                                className="w-full aspect-video object-contain bg-black"
                                controls
                                playsInline
                                poster="/fab.jpg"
                            >
                                <source src="/Demo.mp4" type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6 mt-12">
                            {[
                                { n: '1', title: 'Design with AI', copy: 'Upload fabric or choose from our library, then let AI create stunning fashion designs' },
                                { n: '2', title: 'Visualize Designs', copy: 'See your designs on digital twins of actual textiles for realistic preview' },
                                { n: '3', title: 'Source & Connect', copy: 'Connect directly with fabric suppliers to bring your designs to life' },
                            ].map(({ n, title, copy }) => (
                                <div key={n} className="text-center space-y-3">
                                    <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto font-mono text-sm font-medium border border-gold/40 bg-gold/10 text-gold">
                                        {n}
                                    </div>
                                    <h4 className="luxe-display text-xl text-white">{title}</h4>
                                    <p className="text-sm text-white/50 font-light leading-relaxed">{copy}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="relative py-20 px-6 bg-noir luxe-grain border-t border-white/5">
                <div className="container mx-auto max-w-6xl">
                    <div className="text-center space-y-5 mb-16">
                        <BookOpen className="w-10 h-10 text-gold mx-auto mb-2" />
                        <h2 className="luxe-display text-4xl md:text-5xl text-white">
                            Step by Step <span className="luxe-gold-text">Guide</span>
                        </h2>
                        <p className="text-xl text-white/50 font-light">
                            Master AI-driven fashion design and fabric sourcing with our comprehensive guide
                        </p>
                    </div>

                    <div className="max-w-5xl mx-auto">
                        <div className="luxe-glass p-6 sm:p-8">
                            <div className="rounded-[16px] overflow-hidden mb-6 border border-white/10 bg-white/[0.02]">
                                <div className="relative aspect-[16/10] flex items-center justify-center">
                                    <div className="text-center space-y-6 p-8">
                                        <div className="flex justify-center">
                                            <div className="relative">
                                                <div className="w-32 h-32 rounded-[16px] flex items-center justify-center bg-gradient-to-br from-gold-soft to-gold-deep shadow-[0_20px_60px_-20px_rgba(155, 138, 196,0.3)]">
                                                    <FileText className="w-16 h-16 text-white" />
                                                </div>
                                                <div className="absolute -top-2 -right-2 bg-white text-noir text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                                    PDF
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="luxe-display text-2xl text-white">
                                                Fashdeck Complete Guide
                                            </h3>
                                            <p className="text-white/50 font-light max-w-md mx-auto">
                                                A comprehensive guide to AI-driven fashion design, visualization, and fabric sourcing
                                            </p>
                                        </div>

                                        <div className="flex items-center justify-center gap-6 text-sm text-white/50">
                                            <div className="flex items-center gap-2">
                                                <BookOpen className="w-4 h-4 text-gold" />
                                                <span>Complete Guide</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Check className="w-4 h-4 text-gold" />
                                                <span>Easy to Follow</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <a
                                    href="/guide.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full sm:w-auto luxe-btn-gold group"
                                >
                                    <Eye className="w-5 h-5 relative z-10" />
                                    View Guide
                                </a>
                                <a
                                    href="/guide.pdf"
                                    download="Fashdeck-Guide.pdf"
                                    className="w-full sm:w-auto luxe-btn-ghost group"
                                >
                                    <Download className="w-5 h-5 group-hover:translate-y-0.5 transition-transform" />
                                    Download PDF
                                </a>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6 mt-12">
                            {[
                                { emoji: '🎨', title: 'AI Design Studio', copy: 'Learn to create stunning fashion designs with AI technology' },
                                { emoji: '📸', title: 'Fabric Library', copy: 'Explore and work with thousands of real fabric textures' },
                                { emoji: '🔗', title: 'Supplier Connection', copy: 'Connect with verified suppliers to source your fabrics' },
                            ].map(({ emoji, title, copy }) => (
                                <div key={title} className="text-center space-y-3 p-6 luxe-glass hover:luxe-hairline-gold transition-colors duration-500">
                                    <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto border border-gold/30 bg-gold/5">
                                        <span className="text-2xl">{emoji}</span>
                                    </div>
                                    <h4 className="luxe-display text-xl text-white">{title}</h4>
                                    <p className="text-sm text-white/50 font-light">{copy}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
            </>
            )}

            {/* Kit Detail Modal */}
            {selectedKit && (
                <div
                    className="fixed inset-0 bg-noir/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedKit(null)}
                >
                    <div
                        className="luxe-glass-strong max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="luxe-display text-2xl text-white">Fabric Details</h2>
                                <button
                                    onClick={() => setSelectedKit(null)}
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    <X size={24} className="text-white/70" />
                                </button>
                            </div>
                            
                            {/* Fabric Image */}
                            {(() => {
                                const fabricImage = selectedKit.productDetails?.image || selectedKit.productDetails?.originalImage;
                                if (!fabricImage) return null;
                                return (
                                    <div className="mb-6">
                                        <img
                                            src={fabricImage}
                                            alt="Fabric"
                                            className="w-full h-auto rounded-xl object-contain bg-white/5 ring-1 ring-white/10"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                );
                            })()}

                            {/* WhatsApp Button */}
                            {(() => {
                                const fabricImage = selectedKit.productDetails?.image || selectedKit.productDetails?.originalImage;
                                const sellerPhone = selectedKit.user?.phone;
                                
                                if (!sellerPhone) return null;

                                const handleWhatsApp = () => {
                                    const message = "Hey, I'm interested in this fabric";
                                    const normalizedPhone = sellerPhone.replace(/\D/g, '');
                                    const phoneWithCountryCode = normalizedPhone.startsWith('91') ? normalizedPhone : `91${normalizedPhone}`;
                                    
                                    // For WhatsApp web, we include the image URL in the message
                                    // WhatsApp will automatically generate a preview if the URL is accessible
                                    let whatsappMessage = message;
                                    if (fabricImage) {
                                        whatsappMessage = `${message}\n\n${fabricImage}`;
                                    }
                                    
                                    const whatsappUrl = `https://wa.me/${phoneWithCountryCode}?text=${encodeURIComponent(whatsappMessage)}`;
                                    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
                                };

                                return (
                                    <button
                                        onClick={handleWhatsApp}
                                        className="w-full py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold flex items-center justify-center gap-3 transition-all shadow-sm"
                                    >
                                        <MessageCircle size={24} />
                                        Connect with Vendor on WhatsApp
                                    </button>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Minimal Footer */}
            <footer className="py-14 px-6 bg-noir text-white border-t border-white/10">
                <div className="container mx-auto text-center">
                    <div className="mb-5 flex flex-col items-center gap-3">
                        <div className="h-16 w-16 rounded-2xl overflow-hidden bg-white flex items-center justify-center shadow-[0_0_40px_rgba(155, 138, 196,0.2)]">
                            <img src="/logo.png?v=20260711" alt="Fashdeck" className="h-full w-full object-contain" />
                        </div>
                        <div className="luxe-display text-2xl text-white">
                            Fashdeck
                        </div>
                    </div>
                    <div className="mb-5 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
                        <button
                            type="button"
                            onClick={() => setActiveView('about-us')}
                            className="text-sm text-white/45 hover:text-gold transition-colors"
                        >
                            About Us
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveView('privacy-policy')}
                            className="text-sm text-white/45 hover:text-gold transition-colors"
                        >
                            Privacy Policy
                        </button>
                    </div>
                    <p className="text-white/30 font-mono text-[11px] tracking-[0.15em] uppercase">
                        © {new Date().getFullYear()} Fashdeck · AI-driven fashion designing and fabric sourcing
                    </p>
                </div>
            </footer>

            <CalendlyDemoModal
                isOpen={showCalendlyDemo}
                onClose={() => setShowCalendlyDemo(false)}
            />
        </div>
        {mascotHelp}
        </>
    );
};

export default LandingPage; 
